import type { MenuProps } from 'antd'
import { Dropdown as AntDropdown } from 'antd'

import AccountDetail from '@/assets/icons/accountDetail.svg?react'
import { Trans } from '@/components'
import { TeamRole } from '@/consts/team'
import { useTeamUserRole } from '@/stores/team'
import { useUserStore } from '@/stores/user'

import type { MemberStatus } from '../types/types'

type Row = {
  id: string
  name: string
  username: string
  status: MemberStatus
  role: TeamRole
}

type Item = NonNullable<MenuProps['items']>[number]

export function RowActionsDropdown({
  row,
  onAssignCredits,
  onReclaimCredits,
  onSuspendToggle,
  onDelete,
  disabled,
}: {
  row: Row
  onAssignCredits: (id: string) => void
  onReclaimCredits: (id: string) => void
  onSuspendToggle: (id: string, isCurrentlyInactive: boolean) => void
  onDelete: (id: string) => void
  disabled?: boolean
}) {
  const userRole = useTeamUserRole()
  const currentUserName = useUserStore((s) => s.name)
  const isInactive = row.status === 'inactive'
  const isSelf = row.name === currentUserName

  const isCreditManager = (userRole === TeamRole.Owner || userRole === TeamRole.Admin) && !isSelf
  const canAssign = isCreditManager && row.role >= userRole
  const canReclaim = isCreditManager && row.role > userRole
  const canSuspend = userRole === TeamRole.Owner && !isSelf
  const canDelete = userRole === TeamRole.Owner && !isSelf

  const topActions: Item[] = []
  if (canAssign) {
    topActions.push({
      key: 'assign',
      label: (
        <span className="t-sb text-primary">
          <Trans i18nKey="team.member.assignCredits">Assign Credits</Trans>
        </span>
      ),
    })
  }
  if (canAssign) {
    topActions.push({
      key: 'reclaim',
      disabled: !canReclaim,
      label: (
        <span className={`t-sb ${canReclaim ? 'text-primary' : 'text-tertiary'}`}>
          <Trans i18nKey="team.member.reclaimCredits">Reclaim Credits</Trans>
        </span>
      ),
    })
  }
  if (canSuspend) {
    topActions.push({
      key: 'suspend',
      label: (
        <span className="t-sb">
          <Trans
            i18nKey={isInactive ? 'team.member.unsuspendAccount' : 'team.member.suspendAccount'}
          >
            {isInactive ? 'Unsuspend Account' : 'Suspend Account'}
          </Trans>
        </span>
      ),
    })
  }

  const bottomActions: Item[] = []
  if (canDelete) {
    bottomActions.push({
      key: 'delete',
      label: (
        <span className="t-sb text-error">
          <Trans i18nKey="team.member.deleteAccounts">Delete Accounts</Trans>
        </span>
      ),
    })
  }

  const items: Item[] = [
    ...topActions,
    ...(bottomActions.length ? ([{ type: 'divider' }] as Item[]) : []),
    ...bottomActions,
  ]

  const onClick: MenuProps['onClick'] = ({ key, domEvent }) => {
    domEvent.stopPropagation()
    if (key === 'assign') onAssignCredits(row.id)
    if (key === 'reclaim') onReclaimCredits(row.id)
    if (key === 'suspend') onSuspendToggle(row.id, isInactive)
    if (key === 'delete') onDelete(row.id)
  }

  return (
    <AntDropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{ items, onClick }}
      overlayClassName="row-action-dropdown"
    >
      <button
        type="button"
        disabled={disabled || items.length === 0}
        className="p-1 disabled:opacity-50"
        onClick={(e) => e.stopPropagation()}
        aria-label="Row actions"
        title="Actions"
      >
        <AccountDetail className="text-primary w-4 h-4 cursor-pointer" />
      </button>
    </AntDropdown>
  )
}
